import React from "react";
import { useInput, InputProps } from "react-admin";
import { TextField, Box, InputLabel, FormHelperText } from "@mui/material";

const MONTHS = [
  { value: 1, label: "Januari" },
  { value: 2, label: "Februari" },
  { value: 3, label: "Mars" },
  { value: 4, label: "April" },
  { value: 5, label: "Maj" },
  { value: 6, label: "Juni" },
  { value: 7, label: "Juli" },
  { value: 8, label: "Augusti" },
  { value: 9, label: "September" },
  { value: 10, label: "Oktober" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const DAYS_IN_MONTH: Record<number, number> = {
  1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30,
  7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31,
};

interface MonthDayInputProps extends Omit<InputProps, "source"> {
  source: string;
  label?: string;
}

/**
 * A custom React Admin input that shows month+day selectors instead of a
 * full date picker.  The underlying value is still stored as a full ISO date
 * string (with a fixed year of 2000) so the existing Date column in the
 * database keeps working.
 */
export const MonthDayInput = (props: MonthDayInputProps) => {
  const { source, label } = props;

  const {
    field,
    fieldState: { error },
    isRequired,
  } = useInput({ ...props, source });

  // Parse the current value into month/day
  const currentDate = field.value ? new Date(field.value) : null;
  const currentMonth = currentDate ? currentDate.getMonth() + 1 : 1;
  const currentDay = currentDate ? currentDate.getDate() : 1;

  const maxDay = DAYS_IN_MONTH[currentMonth] || 31;

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMonth = parseInt(e.target.value, 10);
    const newMaxDay = DAYS_IN_MONTH[newMonth] || 31;
    const day = Math.min(currentDay, newMaxDay);
    // Use fixed year 2000 — only month+day matter
    const newDate = new Date(2000, newMonth - 1, day);
    field.onChange(newDate.toISOString());
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDay = Math.max(1, Math.min(parseInt(e.target.value, 10) || 1, maxDay));
    const newDate = new Date(2000, currentMonth - 1, newDay);
    field.onChange(newDate.toISOString());
  };

  return (
    <Box sx={{ mb: 2 }}>
      <InputLabel shrink sx={{ mb: 0.5 }}>
        {label || source}
        {isRequired && " *"}
      </InputLabel>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <TextField
          select
          label="Månad"
          value={currentMonth}
          onChange={handleMonthChange}
          size="small"
          sx={{ minWidth: 150 }}
          SelectProps={{ native: true }}
        >
          {MONTHS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </TextField>
        <TextField
          type="number"
          label="Dag"
          value={currentDay}
          onChange={handleDayChange}
          size="small"
          sx={{ width: 80 }}
          inputProps={{ min: 1, max: maxDay }}
        />
      </Box>
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </Box>
  );
};
